/**
 * Ein Eintrag der Navigation (REQ-20 §3.2).
 *
 * Einträge, die auf eine Seite führen, sind `<a>`-Elemente (`<Link>`) — damit
 * bleiben Mittelklick, „In neuem Tab öffnen" und die Statuszeile des Browsers
 * erhalten. Einträge, die nur eine Aktion auslösen (Administration,
 * Einklappen), sind `<button>`-Elemente. Beide sind ohne `role`/`tabIndex`
 * tastaturbedienbar (REQ-20 §9).
 *
 * Die Beschriftung steht im Mini-Modus auf `display: none` (`--navlabel`);
 * `title` hält den Namen dort erreichbar.
 */

import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';

import { cx } from '@/components/ui/classNames';

export interface NavItemProps {
  /** Symbol aus `navIcons.tsx`. */
  icon: ReactElement;
  label: string;
  /** Ziel des Eintrags; ohne Ziel entsteht ein `<button>`. */
  to?: string;
  /** `title` des Eintrags; Standard ist die Beschriftung. */
  title?: string;
  /** Markiert den Eintrag der gerade offenen Route. */
  active?: boolean;
  /** Nur für `<button>`-Einträge. */
  onClick?: () => void;
  /** Ansichten ohne eigene Seite sind nicht anwählbar. */
  disabled?: boolean;
  className?: string;
}

export function NavItem(props: NavItemProps): ReactElement {
  const className = cx('iwm-nav-item', props.active === true && 'is-active', props.className);
  const title = props.title ?? props.label;

  const content = (
    <>
      {props.icon}
      <span className="iwm-nav-item-label">{props.label}</span>
    </>
  );

  if (props.to !== undefined && props.disabled !== true) {
    return (
      <Link
        to={props.to}
        className={className}
        title={title}
        aria-current={props.active === true ? 'page' : undefined}
      >
        {content}
      </Link>
    );
  }

  return (
    <button
      type="button"
      className={className}
      title={title}
      disabled={props.disabled}
      onClick={props.onClick}
    >
      {content}
    </button>
  );
}

export default NavItem;
